import React, { useEffect, useRef, useCallback } from 'react';
import { setDataAttr } from '../../../utils/helper';
import './LazyLoadImage.scss';

const LazyLoadImage = ({ alt = '', style = {}, className = '', src, gaObj }) => {
  // References to manage image element, throttle timeout, and Intersection Observer
  const imgRef = useRef(null);
  const lazyloadThrottleTimeout = useRef(null);
  const imageObserver = useRef(null);

  // Function to handle scrolling for browsers without Intersection Observer support
  const handleScroll = useCallback(() => {
    if (lazyloadThrottleTimeout.current) {
      clearTimeout(lazyloadThrottleTimeout.current);
    }
    lazyloadThrottleTimeout.current = setTimeout(() => {
      const scrollTop = window.pageYOffset;
      if (imgRef.current && imgRef.current.offsetTop < window.innerHeight + scrollTop) {
        imgRef.current.src = imgRef.current.dataset.src;
        imgRef?.current?.classList?.remove('lazy');
      }
      if (!imgRef.current) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationChange', handleScroll);
      }
    }, 20);
  }, []);

  // Function to handle lazy loading using Intersection Observer
  const handleIntersectionObserver = useCallback(() => {
    imageObserver.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          imgRef.current.src = imgRef.current.dataset.src;
          imgRef.current.onload = () => {
            imgRef?.current?.classList?.remove('lazy');
          };
          imageObserver.current.unobserve(imgRef.current);
        }
      });
    });
    imageObserver.current.observe(imgRef.current);
  }, []);

  // useEffect hook to set up Intersection Observer or fallback event listeners on mount
  useEffect(() => {
    if ('IntersectionObserver' in window) {
      handleIntersectionObserver();
    } else {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationChange', handleScroll);
    }

    // Cleanup function to remove event listeners and observers on unmount
    return () => {
      if (imageObserver.current) {
        imageObserver.current.unobserve(imgRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('orientationChange', handleScroll);
    };
  }, [handleScroll, handleIntersectionObserver]);

  // useEffect hook to handle updates to the image source
  useEffect(() => {
    if ('IntersectionObserver' in window) {
      handleIntersectionObserver();
    }
  }, [src, handleIntersectionObserver]);

  // Set data attributes for the image if `gaObj` is provided
  const dataAttr = gaObj && Object.keys(gaObj).length > 0 ? setDataAttr(gaObj) : null;

  return (
    <img
      {...dataAttr}
      ref={imgRef}
      alt={alt}
      className={`lazy ${className}`}
      data-src={src}
      src={__CLIENT__ ? src : src}
      style={style}
    />
  );
};

export default LazyLoadImage;
